import { useEffect, useState } from 'react'
import { Box, Heading, Segmented, Table, Td, Text, TextLink, Th, Tr } from '@ui'
import { getJSON } from '../api.js'
import { formatTime } from '../format.js'

// The walk's weather (2026-10-25), as the app's walk page shows it (WalkWeatherSection.kt): what
// each source forecast at Start for the walk's hours, what the nearest Met Office station observed,
// and Open-Meteo's estimate taken afterwards, as one summary table; then the same sources hour by
// hour. Built on the server from the stored records (GET /api/walks/:id/weather,
// internal/weather). Wind follows the distance unit (mph with miles). Nothing when no record
// covers the walk.

// Rounded half away from zero on the number's shortest decimal form, as the app's String.format
// does (8.85 → 8.9, where toFixed gives 8.8 by rounding the binary value just below 8.85).
function fmt(v, digits = 1) {
  if (v == null) return '–'
  const abs = Math.abs(v)
  if (String(abs).includes('e')) return v.toFixed(digits) // tiny or huge: nothing to round half
  const rounded = Number(`${Math.round(Number(`${abs}e${digits}`))}e-${digits}`)
  return (v < 0 && rounded > 0 ? -rounded : rounded).toFixed(digits)
}
const plusHour = (iso) => new Date(new Date(iso).getTime() + 3600_000).toISOString()

function WeatherTable({ rows, unit, summary }) {
  const wind = (kmh) => (kmh == null ? '–' : Math.round(unit === 'mi' ? kmh / 1.609344 : kmh))
  return (
    <Table responsive middle autoWidth className="weather-table" mb={1}>
      <thead>
        <tr><th /><Th numeric>°C</Th><Th numeric>Rain</Th><Th numeric>mm</Th><Th numeric>Wind</Th><Th numeric>Gust</Th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const firstActual = r.kind !== 'forecast' && rows[i - 1]?.kind === 'forecast'
          const actual = r.kind !== 'forecast'
          let wet = null
          if (r.kind === 'observed') {
            if (r.wet_hours == null) wet = '–'
            else if (!summary) wet = r.wet_hours > 0 ? 'wet' : 'dry'
            else wet = r.wet_hours === 0 ? 'dry' : `wet ${r.wet_hours} of ${r.hour_count}`
          }
          return (
            <Tr key={`${r.kind}-${r.label}`} className={firstActual ? 'weather-actual-first' : undefined}>
              <Td nowrap weight={actual ? 'semibold' : undefined}>
                {r.slot != null && <span className="forecast-dot" style={{ background: `var(--forecast-${r.slot})` }} aria-hidden="true" />}
                {r.label}
              </Td>
              <Td numeric weight={actual ? 'semibold' : undefined}>{fmt(r.temperature_c)}</Td>
              {r.kind === 'observed' ? (
                // Stations report whether it rained, not how much: one cell over rain and mm.
                <Td numeric colSpan={2} weight="semibold">{wet}</Td>
              ) : (
                <>
                  <Td numeric weight={actual ? 'semibold' : undefined}>{r.rain_chance == null ? '–' : `${Math.round(r.rain_chance)}%`}</Td>
                  <Td numeric weight={actual ? 'semibold' : undefined}>{fmt(r.rain_mm)}</Td>
                </>
              )}
              <Td numeric weight={actual ? 'semibold' : undefined}>{wind(r.wind_kmh)}</Td>
              <Td numeric weight={actual ? 'semibold' : undefined}>{wind(r.gust_kmh)}</Td>
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}

export default function WalkWeather({ walkId, unit }) {
  const [weather, setWeather] = useState(undefined) // undefined loading, null none
  const [hour, setHour] = useState(0)

  useEffect(() => {
    setWeather(undefined)
    setHour(0)
    getJSON(`/api/walks/${walkId}/weather`).then(setWeather).catch(() => setWeather(null))
  }, [walkId])

  if (!weather) return null
  const span = `${formatTime(weather.first_hour)}–${formatTime(plusHour(weather.last_hour))}`
  const hasForecasts = weather.rows.some((r) => r.kind === 'forecast')
  const observed = weather.rows.some((r) => r.kind === 'observed')
  const estimated = weather.rows.some((r) => r.kind === 'estimate')
  const windUnit = unit === 'mi' ? 'mph' : 'km/h'
  const hours = weather.hours
  const current = hours[Math.min(hour, hours.length - 1)]

  return (
    <>
      <Heading level={2} size={5} mt={4}>Weather</Heading>
      <Text as="p" muted text="small" mb={2}>
        {hasForecasts
          ? `Forecasts for ${span}${weather.forecast_taken_at ? `, taken at ${formatTime(weather.forecast_taken_at)}` : ''}.`
          : `The walk's hours, ${span}. No forecasts were stored for this walk.`}
      </Text>
      <WeatherTable rows={weather.rows} unit={unit} summary />
      <Text as="p" muted text="small" mb={1}>
        Mean temperature and wind over the walk&apos;s hours; highest chance of rain, total rain and strongest gust.
        Wind in {windUnit}.
        {observed && ` Observed: hourly readings ${[weather.observed_from, weather.observed_to].filter(Boolean).map(formatTime).filter((t, i, a) => a.indexOf(t) === i).join('–')} at the nearest Met Office station${weather.observation_site ? ` (${weather.observation_site})` : ''}. Stations report whether it rained, not how much.`}
        {estimated && " Estimated: Open-Meteo's model estimate of the walk's hours, taken afterwards. Not a measurement."}
        {weather.observation_note && ` ${weather.observation_note}`}
      </Text>

      {hours.length > 1 && hours.some((h) => h.rows.length > 0) && (
        <>
          <Heading level={3} size={6} mt={3}>Hour by hour</Heading>
          <Box className="weather-hours" mb={2}>
            <Segmented
              name="weather-hour" size="sm" value={String(hour)} onChange={(v) => setHour(Number(v))}
              options={hours.map((h, i) => ({ value: String(i), label: formatTime(h.start) }))}
            />
          </Box>
          <Text as="p" muted text="small" mb={1}>
            {formatTime(current.start)}–{formatTime(plusHour(current.start))}
            {current.rows.some((r) => r.kind === 'observed') && ` (observed: the ${formatTime(current.start)} reading)`}
          </Text>
          {current.rows.length === 0
            ? <Text as="p" muted text="small">Nothing stored for this hour.</Text>
            : <WeatherTable rows={current.rows} unit={unit} summary={false} />}
        </>
      )}

      <Text as="p" muted text="small" mt={2}>
        {weather.attributions.map((a, i) => (
          <span key={a.text}>
            {i > 0 && ' · '}
            <TextLink href={a.url} target="_blank" rel="noreferrer" linkTone="muted">{a.text}</TextLink>
          </span>
        ))}
      </Text>
    </>
  )
}
